import React from 'react';
import styled from 'styled-components';
import { Bell, ShoppingBag, Store, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../hooks/useNotifications';
import { spacing, fontWeight } from '../../styles/theme';
import WebvonkLoader from '../components/WebvonkLoader';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${p => p.theme.colors.background};
`;

const Content = styled.div`
  padding: 72px ${spacing.lg} ${spacing.xl};
  max-width: 700px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeight.bold};
  color: ${p => p.theme.colors.text};
  letter-spacing: -0.02em;
  margin: 0;
`;

const ClearBtn = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.6;
  transition: opacity 0.15s;
  &:hover { opacity: 1; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NotifItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${p => p.$unread
    ? (p.theme.mode === 'dark' ? 'rgba(249,115,22,0.07)' : 'rgba(249,115,22,0.05)')
    : p.theme.colors.surface};
  border: 1px solid ${p => p.$unread
    ? 'rgba(249,115,22,0.2)'
    : (p.theme.mode === 'light' ? p.theme.colors.border : 'transparent')};
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: background 0.12s;
  &:hover {
    background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  }
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${p => p.$color}22;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$color};
`;

const NotifBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotifTitle = styled.div`
  font-size: 14px;
  font-weight: ${p => p.$unread ? 700 : 500};
  color: ${p => p.theme.colors.text};
  margin-bottom: 2px;
`;

const NotifMessage = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.4;
`;

const NotifTime = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  opacity: 0.5;
  margin-top: 4px;
`;

const UnreadDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f97316;
  flex-shrink: 0;
  margin-top: 6px;
`;

const Empty = styled.div`
  text-align: center;
  padding: 80px 0;
  color: ${p => p.theme.colors.muted};
  opacity: 0.4;
  font-size: 14px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.5;
  margin: 20px 0 8px 4px;
`;

function formatRelTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'zojuist';
  if (m < 60) return `${m} min geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} uur geleden`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'gisteren';
  return `${d} dagen geleden`;
}

function groupByDay(notifications) {
  const groups = {};
  for (const n of notifications) {
    const date = new Date(n.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    let key;
    if (date.toDateString() === today.toDateString()) key = 'Vandaag';
    else if (date.toDateString() === yesterday.toDateString()) key = 'Gisteren';
    else key = date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(n);
  }
  return groups;
}

const TYPE_META = {
  new_order:   { icon: ShoppingBag, color: '#f97316' },
  shop_status: { icon: Store,       color: '#22c55e' },
};

export default function NotificationsScreen() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { notifications, markRead, markAllRead, clearAll } = useNotifications();

  const groups = groupByDay(notifications);
  const groupKeys = Object.keys(groups);

  return (
    <Container>
      <Content>
        <PageHeader>
          <PageTitle>Meldingen</PageTitle>
          {notifications.length > 0 && (
            <ClearBtn onClick={clearAll}>
              <Trash2 size={14} />
              Alles wissen
            </ClearBtn>
          )}
        </PageHeader>

        {notifications.length === 0 ? (
          <Empty>
            <Bell size={32} style={{ marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
            Geen meldingen
          </Empty>
        ) : (
          groupKeys.map(day => (
            <div key={day}>
              <SectionLabel>{day}</SectionLabel>
              <List>
                {groups[day].map(n => {
                  const meta = TYPE_META[n.type] || { icon: Bell, color: '#6366f1' };
                  const Icon = meta.icon;
                  return (
                    <NotifItem
                      key={n.id}
                      $unread={!n.read}
                      $clickable={!!n.orderId}
                      onClick={() => {
                        markRead(n.id);
                        if (n.orderId) navigate(`/KMFC/orders/${n.orderId}`);
                      }}
                    >
                      <IconWrap $color={meta.color}><Icon size={16} /></IconWrap>
                      <NotifBody>
                        <NotifTitle $unread={!n.read}>{n.title}</NotifTitle>
                        <NotifMessage>{n.message}</NotifMessage>
                        <NotifTime>{formatRelTime(n.timestamp)}</NotifTime>
                      </NotifBody>
                      {!n.read && <UnreadDot />}
                    </NotifItem>
                  );
                })}
              </List>
            </div>
          ))
        )}
      </Content>
    </Container>
  );
}
